import { useState, useCallback, useEffect, useMemo } from "react";
import { collection, getDocs, query, doc, updateDoc, Timestamp, where } from "firebase/firestore";
import { db } from "../../../../services/firebase";
import { COLLECTIONS } from "../../../../services/collections";
import { memberDataFromFirestore } from "../../../../models/member";
import { useTenant } from "../../../../hooks/useTenant";
import EmployeeService from "../../../../services/employee_service";
import AuditService, { AUDIT_ACTIONS, AUDIT_MODULES } from "../../../../services/audit_service";
import { toast } from "react-toastify";

/**
 * usePortfolio
 * Gerencia a lógica da página de Carteira de Clientes.
 * Layout Master-Detail: sidebar com employees, detail com membros do employee selecionado.
 */
export const usePortfolio = () => {
    const { idTenant, user } = useTenant();

    const [employees, setEmployees] = useState([]);
    const [members, setMembers] = useState([]);
    const [selectedMemberIds, setSelectedMemberIds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMembers, setLoadingMembers] = useState(false);
    const [isTransferring, setIsTransferring] = useState(false);
    const [transferModalOpen, setTransferModalOpen] = useState(false);

    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");

    // Memoiza os membros filtrados para o searchTerm
    const filteredMembers = useMemo(() => {
        let result = members;

        // Filtro por status
        if (statusFilter && statusFilter !== "all") {
            result = result.filter(m => m.status === statusFilter);
        }

        // Filtro por texto
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            result = result.filter(m => 
                m.fullName?.toLowerCase().includes(term) ||
                m.email?.toLowerCase().includes(term) ||
                m.phone?.includes(term)
            );
        }

        return result;
    }, [members, searchTerm, statusFilter]);

    // Carregar employees ao montar
    useEffect(() => {
        const loadEmployees = async () => {
            if (!idTenant) return;
            try {
                setLoading(true);
                const employeesData = await EmployeeService.getEmployees(idTenant);
                setEmployees(employeesData || []);
            } catch (error) {
                console.error("Erro ao carregar colaboradores:", error);
                toast.error("Erro ao carregar lista de colaboradores.");
            } finally {
                setLoading(false);
            }
        };
        loadEmployees();
    }, [idTenant]);

    // Buscar membros por consultor
    const fetchMembers = useCallback(async (employeeId) => {
        if (!idTenant || !employeeId) {
            setMembers([]);
            return;
        }
        try {
            setLoadingMembers(true);
            setSelectedMemberIds([]);
            setSearchTerm(""); // Reseta a busca ao mudar de employee

            const membersRef = collection(db, "tenants", idTenant, COLLECTIONS.MEMBERS);
            const q = query(
                membersRef, 
                where("consultant.uid", "==", employeeId)
            );
            
            const snapshot = await getDocs(q);
            
            const filtered = [];
            snapshot.docs.forEach(docSnap => {
                const data = memberDataFromFirestore(docSnap.id, docSnap.data());
                filtered.push(data);
            });

            // Ordenar por nome
            filtered.sort((a, b) => (a.fullName || "").localeCompare(b.fullName || ""));
            setMembers(filtered);
        } catch (error) {
            console.error("Erro ao buscar membros da carteira:", error);
            toast.error("Erro ao buscar clientes do consultor.");
        } finally {
            setLoadingMembers(false);
        }
    }, [idTenant]);

    // Toggle de seleção individual
    const toggleMemberSelection = useCallback((idMember) => {
        setSelectedMemberIds(prev =>
            prev.includes(idMember)
                ? prev.filter(id => id !== idMember)
                : [...prev, idMember]
        );
    }, []);

    // Selecionar/Deselecionar todos da view ATUAL
    const toggleSelectAll = useCallback(() => {
        setSelectedMemberIds(prev => {
            // Se estão todos os filtrados selecionados, deselecione todos eles (e mantenha quem mais tava)
            const allFilteredSelected = filteredMembers.every(m => prev.includes(m.id));
            if (allFilteredSelected) {
                return prev.filter(id => !filteredMembers.find(m => m.id === id));
            } else {
                // Adicione os filtrados na seleção, sem repetir
                const addedIds = filteredMembers.map(m => m.id).filter(id => !prev.includes(id));
                return [...prev, ...addedIds];
            }
        });
    }, [filteredMembers]);

    // Transferir membros selecionados para outro employee
    const transferMembers = useCallback(async (targetEmployeeId, currentEmployeeId) => {
        if (!idTenant || !targetEmployeeId || selectedMemberIds.length === 0) return;

        const targetEmployee = employees.find(e => e.id === targetEmployeeId);
        if (!targetEmployee) {
            toast.error("Colaborador de destino não encontrado.");
            return;
        }

        const targetName = targetEmployee.fullName || `${targetEmployee.firstName || ""} ${targetEmployee.lastName || ""}`.trim();
        const sourceEmployee = employees.find(e => e.id === currentEmployeeId);
        const sourceName = sourceEmployee?.fullName || "N/A";

        try {
            setIsTransferring(true);

            const updatePromises = selectedMemberIds.map(async (idMember) => {
                const memberRef = doc(db, "tenants", idTenant, COLLECTIONS.MEMBERS, idMember);
                
                await updateDoc(memberRef, {
                    consultant: {
                        uid: targetEmployeeId,
                        name: targetName,
                        photoUrl: targetEmployee.photoUrl || null
                    },
                    updatedAt: Timestamp.now(),
                    updatedBy: {
                        uid: user?.uid || "system",
                        name: user?.displayName || user?.fullName || "Sistema"
                    }
                });

                const memberData = members.find(m => m.id === idMember);
                AuditService.log({
                    idTenant,
                    user,
                    module: AUDIT_MODULES.MEMBERS,
                    action: AUDIT_ACTIONS.UPDATE,
                    entityId: idMember,
                    entityName: memberData?.fullName || idMember,
                    description: `Consultor do cliente "${memberData?.fullName}" transferido de "${sourceName}" para "${targetName}".`,
                    details: {
                        consultant: { from: sourceName, to: targetName }
                    }
                });
            });

            await Promise.all(updatePromises);

            toast.success(`${selectedMemberIds.length} cliente(s) transferido(s) para ${targetName}!`);
            setSelectedMemberIds([]);
            setTransferModalOpen(false);

            // Recarregar a listagem do employee atual
            await fetchMembers(currentEmployeeId);
        } catch (error) {
            console.error("Erro ao transferir membros:", error);
            toast.error("Erro ao transferir clientes. Tente novamente.");
        } finally {
            setIsTransferring(false);
        }
    }, [idTenant, selectedMemberIds, employees, user, members, fetchMembers]);

    return {
        employees,
        members,
        filteredMembers,
        selectedMemberIds,
        loading,
        loadingMembers,
        isTransferring,
        transferModalOpen,
        setTransferModalOpen,
        searchTerm,
        setSearchTerm,
        statusFilter,
        setStatusFilter,
        actions: {
            fetchMembers,
            toggleMemberSelection,
            toggleSelectAll,
            transferMembers
        }
    };
};
